import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { Text } from '@uhg-abyss/web/ui/Text';
import { styled } from '@uhg-abyss/web/tools/styled';

export type AccountInfoBoxProps = {
    userId: string;
    domain: string;
    isLocked: boolean | null;
    className?: string;
    children?: React.ReactNode;
    hideLockStatus?: boolean;
};

const InfoContainer = styled(Card, {
    display: 'grid',
    minHeight: '6rem',
    minWidth: '16rem',
    gridTemplateColumns: 'minmax(max-content, 1fr) 1fr',
    gridTemplateRows: '1fr 1fr',
    padding: '1rem !important',
    gap: '0.5rem 0.25rem',
});

const LeftElement = styled('div', {
    justifySelf: 'start',
});

const RightElement = styled('div', {
    justifySelf: 'end',
});

const BottomLeftElement = styled(LeftElement, {
    alignSelf: 'end',
});
const BottomRightElement = styled(RightElement, {
    alignSelf: 'end',
});

const AccountStatusBadge = styled(Badge, {
    borderRadius: 'var(--abyss-radii-indicator-borderRadius-all-container)',
});

// Pure presentation - no handlers or business logic; parents compose behavior via className/children.
export const AccountInfoBox = ({
    userId,
    domain,
    isLocked,
    className,
    children,
    hideLockStatus,
}: AccountInfoBoxProps) => {
    const { t } = useTranslation();

    const badgeVariant = useMemo(() => {
        if (isLocked === null) {
            return 'neutral';
        }

        return isLocked
            ? 'error'
            : 'success';
    }, [isLocked]);
    
    const badgeLabel = useMemo(() => {
        if (isLocked === null) {
            return t('accountStatusPending');
        }

        return isLocked
            ? t('accountLocked')
            : t('accountUnlocked');
    }, [isLocked, t]);

    const domainLabel = t('domain', { domainName: domain });


    return (
        <InfoContainer className={className}>
            <LeftElement>
                <Text fontWeight="bold" data-testid="accountInfoUserId">{userId}</Text>
            </LeftElement>

            <RightElement>
                {!hideLockStatus && (
                    <AccountStatusBadge variant={badgeVariant} data-testid="accountInfoStatusBadge">{badgeLabel}</AccountStatusBadge>
                )}
            </RightElement>

            <BottomLeftElement>
                <Text fontSize="sm" color="$textSecondary" data-testid="accountInfoDomain">{domainLabel}</Text>
            </BottomLeftElement>
            
            {children && (
                <BottomRightElement>
                    {children}
                </BottomRightElement>
            )}
        </InfoContainer>
    );
};
